import { useState, type FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dahg0jjoh653ivfvnts0";

type Status = "idle" | "submitting" | "success" | "error";

const topics = ["投递热点线索", "纠正档案内容", "订阅事件简报", "内容合作咨询"];

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [feedback, setFeedback] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setFeedback("提交成功，我们会在核实后回复你。");
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      payload.append(key, String(value));
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const lowered = typeof serverMsg === "string" ? serverMsg.toLowerCase() : "";
      const isSpam = lowered.includes("spam");

      if (!response.ok || parsed?.code !== "OK" || isSpam) {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg.trim()
            ? serverMsg
            : "提交失败，请稍后重试，或直接发送邮件到 contact@guatian-observer.example.com。",
        );
        return;
      }

      setStatus("success");
      setFeedback(parsed?.meta?.message || "提交成功，我们会在核实后回复你。");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常，提交没有成功。请稍后重试，或直接发送邮件给我们。");
    }
  };

  return (
    <form
      id="form-dahg0jjoh653ivfvnts0"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7"
    >
      <h3 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">订阅与线索投递</h3>
      <p className="mt-2 font-body text-sm leading-relaxed text-foreground-600">
        留下邮箱即可订阅事件简报；如果你有线索或发现内容需要纠正，请在内容里说明并附上公开来源链接。
      </p>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-2">
          <span className="font-label text-xs text-foreground-600">你的称呼</span>
          <input
            type="text"
            name="name"
            required
            maxLength={40}
            placeholder="怎么称呼你"
            className="h-11 rounded-md border border-background-200 bg-background-100 px-3.5 font-body text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-300/60"
          />
        </label>
        <label className="flex flex-col gap-2">
          <span className="font-label text-xs text-foreground-600">邮箱</span>
          <input
            type="email"
            name="email"
            required
            placeholder="用于接收回复与简报"
            className="h-11 rounded-md border border-background-200 bg-background-100 px-3.5 font-body text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-300/60"
          />
        </label>
      </div>

      <label className="mt-4 flex flex-col gap-2">
        <span className="font-label text-xs text-foreground-600">提交类型</span>
        <select
          name="topic"
          defaultValue={topics[0]}
          className="h-11 rounded-md border border-background-200 bg-background-100 px-3.5 font-body text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-300/60"
        >
          {topics.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>
      </label>

      <label className="mt-4 flex flex-col gap-2">
        <span className="font-label text-xs text-foreground-600">具体内容（最多 500 字）</span>
        <textarea
          name="message"
          required
          rows={5}
          maxLength={500}
          placeholder="请描述你的线索、纠正建议或想订阅的内容方向"
          className="resize-none rounded-md border border-background-200 bg-background-100 px-3.5 py-3 font-body text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-300/60"
        />
      </label>

      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="field-website-alt"
      />

      <button
        type="submit"
        disabled={status === "submitting"}
        className="btn-primary mt-6 w-full disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
      >
        {status === "submitting" ? "正在提交…" : "提交"}
        <i className="ri-send-plane-line" aria-hidden="true"></i>
      </button>

      {status === "success" && (
        <p className="mt-4 flex items-start gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-4 py-3 font-body text-sm text-secondary-900">
          <i className="ri-checkbox-circle-line mt-0.5 text-secondary-600" aria-hidden="true"></i>
          {feedback}
        </p>
      )}
      {status === "error" && (
        <p className="mt-4 flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-4 py-3 font-body text-sm text-primary-900">
          <i className="ri-error-warning-line mt-0.5 text-primary-600" aria-hidden="true"></i>
          {formError}
        </p>
      )}
    </form>
  );
}